import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { FileText } from "lucide-react";
import { feedbackApi, type FeedbackItem, type FeedbackStatus } from "../../../api";
import { Accordion } from "../../ui";
import { formatBytes } from "../../../utils";
import { FeedbackDetailHeader } from "./FeedbackDetailHeader";
import { FeedbackResolutionActions } from "./FeedbackResolutionActions";
import { FeedbackResolutionNote } from "./FeedbackResolutionNote";
import { FeedbackSection } from "./FeedbackSection";

type FeedbackDetailProps = {
  feedback: FeedbackItem | null;
  loading: boolean;
  resolutionNote: string;
  saving: boolean;
  onResolutionNoteChange: (value: string) => void;
  onStatusChange: (status: FeedbackStatus) => void;
};

export function FeedbackDetail({
  feedback,
  loading,
  resolutionNote,
  saving,
  onResolutionNoteChange,
  onStatusChange,
}: FeedbackDetailProps) {
  const [attachmentUrls, setAttachmentUrls] = useState<Record<string, string>>({});

  const feedbackId = feedback?.id ?? null;
  const [prevId, setPrevId] = useState<string | null>(feedbackId);
  if (feedbackId !== prevId) {
    setPrevId(feedbackId);
    setAttachmentUrls({});
  }

  useEffect(() => {
    if (!feedback || feedback.attachments.length === 0) return;
    const fid = feedback.id;
    let cancelled = false;
    Promise.all(
      feedback.attachments.map((att) =>
        feedbackApi
          .getAttachmentUrl(fid, att.id)
          .then((res) => [att.id, res.data.url] as const)
          .catch(() => [att.id, ""] as const),
      ),
    ).then((pairs) => {
      if (!cancelled) {
        setAttachmentUrls(Object.fromEntries(pairs.filter(([, url]) => url)));
      }
    });
    return () => {
      cancelled = true;
    };
  }, [feedback]);

  if (loading) return <DetailPanel>Loading details...</DetailPanel>;
  if (!feedback) return <DetailPanel>Select feedback to review.</DetailPanel>;

  const attachments = feedback.attachments;

  return (
    <div className="min-h-[420px] rounded-lg border border-gray-200 bg-white p-4">
      <div className="space-y-5">
        <FeedbackDetailHeader feedback={feedback} />

        <div className="space-y-2">
          <Accordion title="User comment" defaultOpen>
            <FeedbackSection tone="warning">{feedback.comment}</FeedbackSection>
          </Accordion>

          {feedback.previous_user_prompt && (
            <Accordion title="Original prompt">
              <FeedbackSection>
                {feedback.previous_user_prompt}
              </FeedbackSection>
            </Accordion>
          )}

          <Accordion title="Brian answer" defaultOpen>
            <FeedbackSection renderAssistantContent>
              {feedback.answer_snapshot}
            </FeedbackSection>
          </Accordion>

          {feedback.sql_snapshot && (
            <Accordion title="SQL">
              <FeedbackSection isCode>{feedback.sql_snapshot}</FeedbackSection>
            </Accordion>
          )}

          {attachments.length > 0 && (
            <Accordion title={`Attachments (${attachments.length})`}>
              <div className="flex flex-col gap-2">
                {attachments.map((att) => {
                  const url = attachmentUrls[att.id];
                  const isImage = att.content_type.startsWith("image/");
                  if (isImage) {
                    return (
                      <div key={att.id}>
                        {url ? (
                          <a href={url} target="_blank" rel="noreferrer">
                            <img
                              src={url}
                              alt={att.filename}
                              className="max-h-64 rounded-lg border border-gray-200"
                            />
                          </a>
                        ) : (
                          <p className="text-xs text-gray-400">
                            Loading {att.filename}…
                          </p>
                        )}
                      </div>
                    );
                  }
                  return (
                    <a
                      key={att.id}
                      href={url || undefined}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-blue-700 hover:bg-gray-100"
                    >
                      <FileText size={14} className="shrink-0 text-gray-500" />
                      <span className="truncate">{att.filename}</span>
                      <span className="text-xs text-gray-400">
                        {formatBytes(att.size_bytes)}
                      </span>
                    </a>
                  );
                })}
              </div>
            </Accordion>
          )}
        </div>

        <FeedbackResolutionNote
          value={resolutionNote}
          onChange={onResolutionNoteChange}
        />

        <FeedbackResolutionActions
          status={feedback.status}
          saving={saving}
          onStatusChange={onStatusChange}
        />
      </div>
    </div>
  );
}

function DetailPanel({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-[420px] rounded-lg border border-gray-200 bg-white p-4">
      <p className="text-sm text-gray-400">{children}</p>
    </div>
  );
}
